import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import {
  backspace,
  createEmptyDocument,
  createRootCursor,
  deleteRange,
  getContainerAtCursor,
  getSelectedNodes,
  applyInsertion,
  insertSuperscript,
  insertText,
  mapKeyToInsertion,
  moveDown,
  moveLeft,
  moveLeftClamped,
  moveRight,
  moveRightClamped,
  moveUp,
  normalizeSelection,
  parseLatex,
  selectAll,
  serializeToLatex,
  tabForward,
  type KeyDefinition,
  type LatexCursor,
  type LatexDocument,
  type LatexNode,
} from "@ever-tutor/et-ui";

import { isEditableElement } from "@/drawlock/guards";
import { serializeMixedDocument } from "@/features/tutor/utils/serializeMixed";

const MAX_HISTORY = 100;

interface Snapshot {
  doc: LatexDocument;
  cursor: LatexCursor;
}

interface ComposerState extends Snapshot {
  anchor: LatexCursor | null;
  past: Snapshot[];
  future: Snapshot[];
  /** True while consecutive characters are folding into one undo step. */
  typing: boolean;
}

type Direction = "left" | "right" | "up" | "down";

type Action =
  | { type: "insertText"; value: string }
  | { type: "insertNodes"; nodes: LatexNode[] }
  | { type: "panelKey"; key: KeyDefinition }
  | { type: "backspace" }
  | { type: "deleteSelection" }
  | { type: "move"; direction: Direction; extend: boolean }
  | { type: "tab" }
  | { type: "selectAll" }
  | { type: "setSelection"; cursor: LatexCursor }
  | { type: "extendSelection"; offset: number }
  | { type: "endSelection" }
  | { type: "undo" }
  | { type: "redo" }
  | { type: "reset" }
  | { type: "setText"; text: string };

const initialState = (): ComposerState => ({
  doc: createEmptyDocument(),
  cursor: createRootCursor(),
  anchor: null,
  past: [],
  future: [],
  typing: false,
});

const rootCursorAt = (offset: number): LatexCursor => ({
  containerId: "root",
  containerField: "nodes",
  offset,
});

const dispatchPanelKey = (
  doc: LatexDocument,
  cursor: LatexCursor,
  key: KeyDefinition,
): Snapshot => {
  const value = key.value;

  if (value === "BACKSPACE") return backspace(doc, cursor);
  if (value === "CURSOR_LEFT") return { doc, cursor: moveLeft(doc, cursor) };
  if (value === "CURSOR_RIGHT") return { doc, cursor: moveRight(doc, cursor) };
  if (["CLOSE", "SHIFT", "", "UNDO", "REDO"].includes(value)) {
    return { doc, cursor };
  }

  if (value === "^2") {
    const sup = insertSuperscript(doc, cursor);
    const two = insertText(sup.doc, sup.cursor, "2");
    return { doc: two.doc, cursor: tabForward(two.doc, two.cursor) };
  }

  const insertion = mapKeyToInsertion(value);
  return insertion ? applyInsertion(doc, cursor, insertion) : { doc, cursor };
};

export const selectionRange = (state: {
  anchor: LatexCursor | null;
  cursor: LatexCursor;
}) => (state.anchor ? normalizeSelection(state.anchor, state.cursor) : null);

const collapsed = (state: ComposerState): Snapshot => {
  const range = selectionRange(state);
  return range
    ? deleteRange(state.doc, range)
    : { doc: state.doc, cursor: state.cursor };
};

/**
 * One undo step per run of typing, not per character.
 *
 * Every keystroke pushing its own snapshot made Cmd+Z remove a single letter,
 * which reads as nothing happening. A run is broken by whitespace or by any
 * action that is not plain typing.
 */
const commit = (
  state: ComposerState,
  next: Snapshot,
  { typing = false }: { typing?: boolean } = {},
): ComposerState => ({
  ...next,
  anchor: null,
  past:
    typing && state.typing
      ? state.past
      : [...state.past, { doc: state.doc, cursor: state.cursor }].slice(
          -MAX_HISTORY,
        ),
  future: [],
  typing,
});

const moveWith = (
  state: ComposerState,
  direction: Direction,
  extend: boolean,
): ComposerState => {
  const { doc, cursor, anchor } = state;

  if (extend && (direction === "left" || direction === "right")) {
    const move = direction === "left" ? moveLeftClamped : moveRightClamped;
    return { ...state, cursor: move(doc, cursor), anchor: anchor ?? cursor };
  }

  const range = selectionRange(state);
  if (range && (direction === "left" || direction === "right")) {
    return {
      ...state,
      anchor: null,
      cursor: rootCursorAt(
        direction === "left" ? range.startOffset : range.endOffset,
      ),
    };
  }

  const move = { left: moveLeft, right: moveRight, up: moveUp, down: moveDown }[
    direction
  ];
  return { ...state, cursor: move(doc, cursor), anchor: null };
};

const reducer = (state: ComposerState, action: Action): ComposerState => {
  switch (action.type) {
    case "insertText": {
      const base = collapsed(state);
      return commit(state, insertText(base.doc, base.cursor, action.value), {
        typing: action.value.trim() !== "",
      });
    }

    case "insertNodes": {
      const base = collapsed(state);
      const context = getContainerAtCursor(base.doc, base.cursor);
      if (!context) return state;
      context.container.splice(base.cursor.offset, 0, ...action.nodes);
      return commit(state, {
        doc: { ...base.doc },
        cursor: {
          ...base.cursor,
          offset: base.cursor.offset + action.nodes.length,
        },
      });
    }

    case "panelKey": {
      const base = collapsed(state);
      return commit(state, dispatchPanelKey(base.doc, base.cursor, action.key));
    }

    case "backspace":
    case "deleteSelection": {
      const range = selectionRange(state);
      if (range) return commit(state, deleteRange(state.doc, range));
      if (action.type === "deleteSelection") return state;
      return commit(state, backspace(state.doc, state.cursor));
    }

    case "move":
      return {
        ...moveWith(state, action.direction, action.extend),
        typing: false,
      };

    case "tab":
      return {
        ...state,
        cursor: tabForward(state.doc, state.cursor),
        anchor: null,
      };

    case "selectAll": {
      const { anchor, focus } = selectAll(state.doc, state.cursor);
      return { ...state, anchor, cursor: focus };
    }

    case "setSelection":
      return { ...state, cursor: action.cursor, anchor: action.cursor };

    case "extendSelection":
      return state.anchor
        ? { ...state, cursor: rootCursorAt(action.offset) }
        : state;

    case "endSelection":
      return state.anchor && !selectionRange(state)
        ? { ...state, anchor: null }
        : state;

    case "undo": {
      const previous = state.past[state.past.length - 1];
      if (!previous) return state;
      return {
        ...previous,
        anchor: null,
        typing: false,
        past: state.past.slice(0, -1),
        future: [
          ...state.future,
          { doc: state.doc, cursor: state.cursor },
        ].slice(-MAX_HISTORY),
      };
    }

    case "redo": {
      const next = state.future[state.future.length - 1];
      if (!next) return state;
      return {
        ...next,
        anchor: null,
        typing: false,
        past: [...state.past, { doc: state.doc, cursor: state.cursor }].slice(
          -MAX_HISTORY,
        ),
        future: state.future.slice(0, -1),
      };
    }

    case "setText": {
      const doc = parseLatex(action.text);
      return {
        doc,
        cursor: rootCursorAt(doc.nodes.length),
        anchor: null,
        past: [...state.past, { doc: state.doc, cursor: state.cursor }].slice(
          -MAX_HISTORY,
        ),
        future: [],
        typing: false,
      };
    }

    case "reset":
      return initialState();

    default:
      return state;
  }
};

const writeClipboard = (text: string) => {
  if (text && typeof navigator !== "undefined" && navigator.clipboard) {
    navigator.clipboard.writeText(text).catch(() => {});
  }
};

const sameCursor = (a: LatexCursor, b: LatexCursor): boolean =>
  a.containerId === b.containerId &&
  a.containerField === b.containerField &&
  a.offset === b.offset &&
  a.cellIndex === b.cellIndex;

export type ComposerDraft = Snapshot;

export const useMathComposer = ({
  onSubmit,
  draft = null,
}: {
  onSubmit: () => void;
  draft?: ComposerDraft | null;
}) => {
  const [state, dispatch] = useReducer(
    reducer,
    draft,
    (seed: ComposerDraft | null): ComposerState =>
      seed ? { ...initialState(), ...seed } : initialState(),
  );
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const fieldRef = useRef<HTMLDivElement>(null);

  const submitRef = useRef(onSubmit);
  submitRef.current = onSubmit;
  const stateRef = useRef(state);
  stateRef.current = state;

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
    fieldRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const el = fieldRef.current;
      const target = event.target;
      const fromField = !!el && (target === el || el.contains(target as Node));

      if (!fromField) {
        if (!isPanelOpen) return;
        if (isEditableElement(target)) return;
      }

      const { key, shiftKey, ctrlKey, metaKey, altKey, repeat } = event;
      const mod = ctrlKey || metaKey;

      // Requirement 43: while the field has focus the keyboard is for typing
      // only. preventDefault is not enough — tldraw binds every tool and action
      // hotkey to document.body through hotkeys-js, which never looks at it.
      // This is the app's only window-capture listener, so halting the
      // propagation path here is what stops Backspace deleting canvas shapes
      // and "," dispatching a synthetic pointer_down.
      //
      // stopPropagation leaves other listeners on this same node running.
      //
      // Keydown only. drawlock's spaceOwned, useHoldKey's keyOwned and
      // hotkeys-js's _downKeys are all cleared on keyup — swallowing a release
      // strands a latched pen, an open microphone, or a phantom modifier.
      //
      // Escape is the exception: the composer only consumes it to close its own
      // panel, and the exit/confirm modals and colour popover close on it.
      const yieldsEscape = key === "Escape" && !isPanelOpen;
      if (!yieldsEscape && (fromField || mod)) event.stopPropagation();
      const shortcut = key.length === 1 ? key.toLowerCase() : key;
      const current = stateRef.current;

      if (key === "Enter") {
        event.preventDefault();
        if (shiftKey) {
          dispatch({ type: "insertText", value: "\n" });
          return;
        }
        if (repeat || event.isComposing) return;
        submitRef.current();
        return;
      }

      if (key === "Escape") {
        if (!isPanelOpen) return;
        event.preventDefault();
        closePanel();
        return;
      }

      if (key === "Backspace") {
        event.preventDefault();
        dispatch({ type: "backspace" });
        return;
      }

      if (key.startsWith("Arrow")) {
        const direction = key.slice(5).toLowerCase() as Direction;
        if (!["left", "right", "up", "down"].includes(direction)) return;
        event.preventDefault();
        dispatch({ type: "move", direction, extend: shiftKey });
        return;
      }

      if (key === "Tab") {
        // tabForward no-ops at the end of the document, so claiming Tab
        // unconditionally made the field a keyboard trap (WCAG 2.1.2): focus
        // could enter and never leave. Only claim it while there is another
        // LaTeX slot to step into.
        const next = tabForward(current.doc, current.cursor);
        if (sameCursor(next, current.cursor)) return;
        event.preventDefault();
        dispatch({ type: "tab" });
        return;
      }

      if (mod && shortcut === "z" && !shiftKey) {
        event.preventDefault();
        dispatch({ type: "undo" });
        return;
      }

      if (mod && ((shortcut === "z" && shiftKey) || shortcut === "y")) {
        event.preventDefault();
        dispatch({ type: "redo" });
        return;
      }

      if (mod && shortcut === "a") {
        event.preventDefault();
        dispatch({ type: "selectAll" });
        return;
      }

      if (mod && (shortcut === "c" || shortcut === "x")) {
        event.preventDefault();
        const range = selectionRange(current);
        if (!range) {
          if (shortcut === "c") writeClipboard(serializeToLatex(current.doc));
          return;
        }
        writeClipboard(
          serializeToLatex({
            ...current.doc,
            nodes: getSelectedNodes(current.doc, range),
          }),
        );
        if (shortcut === "x") dispatch({ type: "deleteSelection" });
        return;
      }

      if (mod && shortcut === "v") {
        event.preventDefault();
        navigator.clipboard
          ?.readText()
          .then((text) => {
            const nodes = parseLatex(text).nodes;
            if (nodes.length) dispatch({ type: "insertNodes", nodes });
          })
          .catch(() => {});
        return;
      }

      if (key.length === 1 && !ctrlKey && !metaKey && !altKey) {
        event.preventDefault();
        dispatch({ type: "insertText", value: key });
      }
    };

    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [closePanel, isPanelOpen]);

  useEffect(() => {
    if (!isPanelOpen) return;

    const onMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      if (target.closest('[data-custom-keyboard="true"]')) return;
      if (target.closest("[data-chat-input-bar]")) return;
      setIsPanelOpen(false);
    };

    const id = setTimeout(() =>
      document.addEventListener("mousedown", onMouseDown),
    );
    return () => {
      clearTimeout(id);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [isPanelOpen]);

  return {
    fieldRef,
    doc: state.doc,
    cursor: state.cursor,
    selectionAnchor: state.anchor,
    text: serializeMixedDocument(state.doc),
    isEmpty: state.doc.nodes.length === 0,
    reset: useCallback(() => dispatch({ type: "reset" }), []),
    setText: useCallback(
      (text: string) => dispatch({ type: "setText", text }),
      [],
    ),
    onKeyPress: useCallback((key: KeyDefinition) => {
      if (key.value === "UNDO") {
        dispatch({ type: "undo" });
        return;
      }
      if (key.value === "REDO") {
        dispatch({ type: "redo" });
        return;
      }
      if (key.value === "CLOSE") {
        setIsPanelOpen(false);
        fieldRef.current?.focus();
        return;
      }
      dispatch({ type: "panelKey", key });
    }, []),
    beginSelection: useCallback(
      (cursor: LatexCursor) => dispatch({ type: "setSelection", cursor }),
      [],
    ),
    extendSelection: useCallback(
      (offset: number) => dispatch({ type: "extendSelection", offset }),
      [],
    ),
    endSelection: useCallback(() => dispatch({ type: "endSelection" }), []),
    isActive: isFocused || isPanelOpen,
    setFocused: setIsFocused,
    isPanelOpen,
    togglePanel: () => setIsPanelOpen((open) => !open),
    closePanel,
  };
};
